// ============================================================================
// RealRateCard (F-754) — taux horaire reel, append-only (M-756)
// ============================================================================
// Pas de garde locale (M-778) : canSetHourlyRate est deja evalue serveur ; le hub
// n'affiche cette carte que si can.setHourlyRate. Hors grille sans taux -> la carte
// s'emphase (le taux reel EST la source de paie).

import { useState } from 'react';
import { GS_COMPONENTS } from '@gs/ui';
import { Save } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Card, CardHeader, Badge } from './ui';
import { useAddRate, useDeleteRate, managementErrorCode, type RateChange } from '../../hooks/useManagement';

const RATE_REASONS = ['hire', 'adjustment', 'promotion', 'grid_alignment', 'off_grid'] as const;
// Exemple numerique de l'<input type="number"> (valeur au format du champ, point
// decimal) : identique en FR et en EN, aucun texte a traduire.
const RATE_PLACEHOLDER = '32.50';

export function RealRateCard({
  email,
  rateHistory,
  offGrid,
  onError,
}: {
  email: string;
  rateHistory: RateChange[];
  offGrid: boolean;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const add = useAddRate();
  const del = useDeleteRate();

  const sorted = [...rateHistory].sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
  const current = sorted[0] ?? null;

  const [rate, setRate] = useState('');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState<string>(offGrid ? 'off_grid' : 'adjustment');
  const [note, setNote] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);

  function submit() {
    if (!rate || !date) {
      setStepError(t('gest:management.errors.invalid'));
      return;
    }
    setStepError(null);
    add.mutate(
      { email, payload: { hourlyRate: Number(rate), effectiveDate: date, reason, note: note.trim() || null } },
      {
        onSuccess: () => { setRate(''); setDate(''); setNote(''); },
        onError: (err) => onError(managementErrorCode(err)),
      },
    );
  }

  const labelCls = 'text-meta font-medium text-slate-600 dark:text-slate-300';

  return (
    <Card emphasis={offGrid && !current}>
      <CardHeader
        title={t('gest:management.rate.title')}
        action={
          <Badge tone={current ? 'info' : offGrid ? 'warning' : 'neutral'}>
            {current ? t('gest:management.rate.since', { rate: current.hourlyRate.toFixed(2), date: current.effectiveDate }) : t('gest:management.rate.undefined')}
          </Badge>
        }
      />
      {offGrid && !current ? (
        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-field text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          {t('gest:management.rate.offGridRequired')}
        </p>
      ) : null}

      <form className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rate-amount" className={labelCls}>{t('gest:management.rate.amount')}</label>
          <input id="rate-amount" type="number" min={0} step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} placeholder={RATE_PLACEHOLDER} className={GS_COMPONENTS.input} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rate-date" className={labelCls}>{t('gest:management.rate.effectiveDate')}</label>
          <input id="rate-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={GS_COMPONENTS.input} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rate-reason" className={labelCls}>{t('gest:management.rate.reason')}</label>
          <select id="rate-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={GS_COMPONENTS.input}>
            {RATE_REASONS.map((r) => (
              <option key={r} value={r}>{t(`gest:management.rate.reasons.${r}`)}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rate-note" className={labelCls}>{t('gest:management.rate.note')}</label>
          <input id="rate-note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} className={GS_COMPONENTS.input} />
        </div>
        {stepError ? <p role="alert" className="col-span-2 text-field text-red-600 dark:text-red-400">{stepError}</p> : null}
        <div className="col-span-2 flex justify-end">
          <button type="submit" disabled={add.isPending} className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}>
            <Save size={16} aria-hidden="true" />
            {t('gest:management.rate.save')}
          </button>
        </div>
      </form>

      {sorted.length > 0 ? (
        <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-field">
            <thead>
              <tr className="bg-slate-50 text-chip uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
                <th className="px-3 py-2 text-left font-medium">{t('gest:management.rate.thDate')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('gest:management.rate.thRate')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('gest:management.rate.thReason')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('gest:management.rate.thNote')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className="border-t border-slate-200 dark:border-slate-700">
                  <td className="px-3 py-2 tabular-nums text-slate-700 dark:text-slate-200">{r.effectiveDate}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{r.hourlyRate.toFixed(2)} $</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{t(`gest:management.rate.reasons.${r.reason}`, r.reason)}</td>
                  <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{r.note ?? '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => del.mutate({ email, payload: { rateId: r.id } }, { onError: (err) => onError(managementErrorCode(err)) })} className={`${GS_COMPONENTS.btnGhost} text-red-600 dark:text-red-400`}>
                      {t('gest:management.common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-3 text-field text-slate-500 dark:text-slate-400">{t('gest:management.rate.none')}</p>
      )}
    </Card>
  );
}
